import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ICertificadoEntidad,
  ICodigoDispositivo,
  IEventoGenerico,
} from 'modelos/src';
import { CommonModule } from '@angular/common';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { CertificadoEntidadService } from '../../../../auxiliares/servicios/http/certificado-entidad.service';

interface ICodigoComparacion {
  codigo: string;
  descripcion: string;
  esperado: boolean;
  generado: boolean;
  cantidadEventos?: number;
  fechaUltimoEvento?: Date;
}

@Component({
  imports: [CommonModule, AuxiliaresModule],
  selector: 'app-ver-certificado-entidad',
  templateUrl: './ver-certificado-entidad.component.html',
  styleUrls: ['./ver-certificado-entidad.component.scss'],
})
export class VerCertificadoEntidadComponent implements OnInit {
  dialogData = inject<{
    certificado: ICertificadoEntidad;
  }>(MAT_DIALOG_DATA, { optional: true });
  dialogRef = inject<MatDialogRef<VerCertificadoEntidadComponent>>(
    MatDialogRef,
    { optional: true },
  );
  certificadoEntidadService = inject(CertificadoEntidadService);

  public certificado?: ICertificadoEntidad;
  public codigosComparacion: ICodigoComparacion[] = [];
  public title = 'Certificado de entidad';
  public columnsToDisplay = [
    'codigo',
    'descripcion',
    'generado',
    'fechaUltimo',
  ];

  ngOnInit(): void {
    if (this.dialogData) {
      this.certificado = this.dialogData.certificado;
      this.procesarDatos();
    }
  }

  private procesarDatos(): void {
    if (!this.certificado) return;

    const codigosEsperados = this.certificado.codigosEsperados || [];
    const eventosRegistrados = this.certificado.eventosRegistrados || [];

    // Crear mapa de eventos por código (usando valores.codigoAlarma o valores.codigoTracker)
    const eventosMap = new Map<string, IEventoGenerico[]>();
    eventosRegistrados.forEach((evento) => {
      let codigo = '';
      // Intentar obtener el código según el tipo de evento
      if ((evento as any).valores?.codigoAlarma) {
        codigo = (evento as any).valores.codigoAlarma;
      } else if ((evento as any).valores?.codigoTracker) {
        codigo = (evento as any).valores.codigoTracker;
      }

      if (codigo) {
        if (!eventosMap.has(codigo)) {
          eventosMap.set(codigo, []);
        }
        eventosMap.get(codigo)?.push(evento);
      }
    });

    // Crear mapa de códigos esperados
    const codigosEsperadosMap = new Map<string, ICodigoDispositivo>();
    codigosEsperados.forEach((codigo) => {
      if (codigo.codigo) {
        codigosEsperadosMap.set(codigo.codigo, codigo);
      }
    });

    // Obtener todos los códigos únicos (esperados + generados)
    const todosLosCodigos = new Set<string>();
    codigosEsperados.forEach((cod) => {
      if (cod.codigo) todosLosCodigos.add(cod.codigo);
    });
    eventosRegistrados.forEach((evt) => {
      const codigo =
        (evt as any).valores?.codigoAlarma ||
        (evt as any).valores?.codigoTracker;
      if (codigo) todosLosCodigos.add(codigo);
    });

    // Construir array de comparación
    this.codigosComparacion = Array.from(todosLosCodigos)
      .map((codigo) => {
        const codigoEsperado = codigosEsperadosMap.get(codigo);
        const eventosDelCodigo = eventosMap.get(codigo) || [];
        const ultimoEvento =
          eventosDelCodigo.length > 0
            ? eventosDelCodigo.reduce((prev, current) => {
                const prevDate = new Date((prev as any).fechaCreacion || 0);
                const currentDate = new Date(
                  (current as any).fechaCreacion || 0,
                );
                return currentDate > prevDate ? current : prev;
              })
            : null;

        return {
          codigo,
          descripcion:
            codigoEsperado?.descripcion ||
            this.obtenerDescripcionDeEvento(eventosDelCodigo[0]) ||
            'Sin descripción',
          esperado: !!codigoEsperado,
          generado: eventosDelCodigo.length > 0,
          cantidadEventos: eventosDelCodigo.length,
          fechaUltimoEvento: ultimoEvento
            ? new Date((ultimoEvento as any).fechaCreacion || '')
            : undefined,
        };
      })
      .sort((a, b) => {
        // Ordenar: primero los esperados, luego por código
        if (a.esperado !== b.esperado) {
          return a.esperado ? -1 : 1;
        }
        return a.codigo.localeCompare(b.codigo);
      });
  }

  private obtenerDescripcionDeEvento(evento?: IEventoGenerico): string {
    if (!evento) return '';
    const categoria = (evento as any).categoria;
    const titulo = (evento as any).valores?.titulo;
    return titulo || categoria || '';
  }

  public getEntidadInfo(): string {
    if (!this.certificado) return 'Sin entidad';

    if (this.certificado.tracker) {
      return `${this.certificado.tracker.identificacion}`;
    }
    if (this.certificado.activo) {
      return `${this.certificado.activo.identificacion}`;
    }
    if (this.certificado.alarma) {
      return `${this.certificado.alarma.nombre}`;
    }
    return 'Sin entidad';
  }

  public getEstadisticas() {
    const codigosEsperados = this.codigosComparacion.filter(
      (c) => c.esperado,
    ).length;
    const codigosGenerados = this.codigosComparacion.filter(
      (c) => c.generado,
    ).length;
    const codigosCumplidos = this.codigosComparacion.filter(
      (c) => c.esperado && c.generado,
    ).length;
    const codigosNoGenerados = this.codigosComparacion.filter(
      (c) => c.esperado && !c.generado,
    ).length;
    const codigosExtra = this.codigosComparacion.filter(
      (c) => !c.esperado && c.generado,
    ).length;

    return {
      codigosEsperados,
      codigosGenerados,
      codigosCumplidos,
      codigosNoGenerados,
      codigosExtra,
      porcentajeCumplimiento:
        codigosEsperados > 0
          ? ((codigosCumplidos / codigosEsperados) * 100).toLocaleString(
              'es-AR',
              { minimumFractionDigits: 1, maximumFractionDigits: 1 },
            )
          : '0',
    };
  }

  public getRowClass(row: ICodigoComparacion): string {
    if (row.esperado && row.generado) {
      return 'row-success'; // Verde: Esperado y generado
    }
    if (row.esperado && !row.generado) {
      return 'row-warning'; // Amarillo: Esperado pero NO generado
    }
    if (!row.esperado && row.generado) {
      return 'row-info'; // Azul: NO esperado pero sí generado
    }
    return '';
  }

  public cerrar(): void {
    this.dialogRef?.close();
  }

  public exportar(): void {
    if (this.certificado) {
      this.certificadoEntidadService.exportar(
        this.certificado._id || '',
        `Certificado_Entidad_${this.getEntidadInfo()}`,
      );
    }
  }
}
